import { css } from '@emotion/react';
import { applyFont, tokens, variables } from '@domain-group/fe-brary';
import { media } from '../../../styles/breakpoints-2026';

const { colors, spacing, typography, borderRadius } = tokens;

// The Figma property card is pinned to the compact size class at every
// breakpoint, but fe-brary typography tokens are responsive and grow at
// tablet and desktop, so the card sets fixed compact sizes and keeps only
// the token font family and weight.
const compactCardFont = (
  token: { family: string; weight: string },
  size: number,
  lineHeight: number,
) => css`
  font-family: ${token.family};
  font-size: ${size}px;
  font-weight: ${token.weight};
  line-height: ${lineHeight}px;
`;

export const section = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing.m};
  min-width: 0;
  padding: ${spacing.l} 0 ${spacing.xl};

  ${media('>=desktop')} {
    padding: 0 0 ${spacing.m};
  }
`;
export const heading = css`
  ${applyFont(typography.heading)}
  color: ${colors.neutral.heavy.default};
  margin: 0;
`;
export const tabsViewport = css`
  min-width: 0;

  ${media('<tablet')} {
    margin-right: calc(-1 * ${spacing.m});
  }

  [role='tab'] {
    ${applyFont(typography.bodyBold)}
    color: ${colors.neutral.heavy.default};
  }

  [role='tab'] > span {
    align-items: center;
    display: inline-flex;
    gap: ${spacing.xxs};
  }

  [role='tab'] > span > span {
    ${applyFont(typography.mini)}

    &::before {
      content: none;
    }
  }
`;
export const zeroTabCount = css`
  ${applyFont(typography.mini)}
  color: ${colors.neutral.heavy.default};

  &::before {
    content: none;
  }
`;
export const listings = css`
  display: grid;
  gap: ${spacing.m};
  grid-template-columns: repeat(3, minmax(0, 1fr));
  min-width: 0;

  ${media('<tablet')} {
    display: flex;
    gap: ${spacing.m};
    margin: 0 calc(-1 * ${spacing.m});
    overflow-x: auto;
    padding: 0 ${spacing.m} ${spacing.xxs};
    scroll-padding: 0 ${spacing.m};
    scroll-snap-type: x mandatory;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;
export const propertyCard = css`
  animation: property-card-in 260ms ease-out both;
  animation-delay: calc(var(--card-index, 0) * 70ms);
  display: flex;
  flex-direction: column;
  min-width: 0;
  position: relative;
  scroll-snap-align: start;

  @keyframes property-card-in {
    from {
      opacity: 0;
      transform: translateY(6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @media (hover: hover) {
    &:hover {
      --property-card-accent: ${colors.interactive.base.hover};
      --property-card-image-scale: 1.05;
    }
  }

  &:active {
    --property-card-accent: ${colors.interactive.base.pressed};
    --property-card-image-scale: 1.05;
  }

  &:has(a:focus-visible) {
    --property-card-accent: ${colors.interactive.base.hover};
    --property-card-ring: ${colors.interactive.base.default};
  }

  ${media('<tablet')} {
    flex: 0 0 223px;
  }
`;
export const cardLink = css`
  border-radius: ${borderRadius.outer};
  color: inherit;
  display: block;
  min-width: 0;
  text-decoration: none;

  &:focus-visible {
    outline: none;
  }
`;
export const imageWrap = css`
  aspect-ratio: 3 / 2;
  background: ${colors.neutral.surface.pressed};
  border-radius: ${borderRadius.outer};
  overflow: hidden;
  position: relative;

  &::after {
    border: 1px solid var(--property-card-ring, transparent);
    border-radius: inherit;
    content: '';
    inset: 0;
    pointer-events: none;
    position: absolute;
  }
`;
export const image = css`
  display: block;
  height: 100%;
  object-fit: cover;
  transform: scale(var(--property-card-image-scale, 1));
  transition: transform 200ms ease-out;
  width: 100%;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
export const imageFallback = css`
  height: 100%;
  width: 100%;
`;
export const statusBadge = (family: 'accentFive' | 'accentOne') => {
  const badgeColors =
    family === 'accentFive' ? colors.accentFive : colors.accentOne;

  return css`
    bottom: ${spacing.s};
    display: flex;
    left: ${spacing.s};
    max-width: calc(100% - 2 * ${spacing.s});
    position: absolute;

    > span {
      background-color: ${badgeColors.trim.default};
      border-radius: ${borderRadius.mini};
      color: ${colors.accentFive.heavy.default};
      max-width: 100%;
      overflow: hidden;
      padding: 2px ${spacing.xxs};
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  `;
};
export const cardContent = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xxs};
  padding: ${spacing.xs} ${spacing.xxs};
`;
export const priceRow = css`
  align-items: center;
  min-height: 28px;
  display: flex;
  gap: ${spacing.xs};
  justify-content: space-between;
`;
export const detailsLink = css`
  color: inherit;
  display: flex;
  flex-direction: column;
  gap: ${spacing.xxs};
  min-width: 0;
  text-decoration: none;

  &:focus-visible {
    outline: none;
  }
`;
export const price = css`
  ${compactCardFont(typography.lead, 18, 24)}
  color: var(--property-card-accent, ${colors.neutral.heavy.default});
  margin: 0;
`;
export const address = css`
  ${compactCardFont(typography.body, 16, 24)}
  color: var(--property-card-accent, ${colors.neutral.medium.default});
  margin: 0;
`;
export const addressLine = css`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
export const propertyDetails = css`
  ${compactCardFont(typography.caption, 14, 20)}
  align-items: center;
  color: var(--property-card-accent, ${colors.neutral.medium.default});
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.xs};
  padding-top: ${spacing.xs};
`;
export const feature = css`
  align-items: center;
  display: inline-flex;
  gap: ${spacing.xxs};
  white-space: nowrap;
`;
export const propertyType = css`
  color: var(--property-card-accent, ${colors.neutral.medium.default});
`;
export const shortlistButton = css`
  align-items: center;
  background: transparent;
  border: 0;
  color: ${colors.neutral.medium.default};
  cursor: pointer;
  display: flex;
  flex: 0 0 auto;
  justify-content: center;
  padding: 0;

  &:hover,
  &:focus-visible {
    color: ${colors.interactive.base.default};
  }

  &:focus-visible {
    outline: 2px solid ${colors.interactive.base.default};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: wait;
    opacity: 0.5;
  }

  &[aria-pressed='true'] > svg {
    color: ${variables.colour.brandYellow};
    fill: ${variables.colour.brandYellow};
  }

  &[aria-pressed='true']:hover > svg {
    color: ${variables.colour.brandYellowDark};
    fill: ${variables.colour.brandYellowDark};
  }

  svg {
    height: 20px;
    width: 20px;
  }
`;
export const loading = css`
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 320px;
`;
export const noResults = css`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: ${spacing.m};
  justify-content: center;
  padding: ${spacing.xl} ${spacing.m};
  text-align: center;
`;
export const noResultsHeading = css`
  ${applyFont(typography.lead)}
  color: ${colors.neutral.heavy.default};
  margin: 0;
`;
export const noResultsCopy = css`
  ${applyFont(typography.caption)}
  color: ${colors.neutral.medium.default};
  margin: 0;
`;
export const pagination = css`
  display: flex;
  justify-content: center;
`;
